<script setup lang="ts">
import { formatDate, formatKey, truncate, truncateMiddle } from '@shared/utils'
import {
  peerAuthorizationColor,
  peerAuthorizationLabel,
} from '@shared/constants/trustlineAuthorization'
import type { CryptoWallet } from '../types/cryptoWalletTypes'

import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import CopyButton from '@uiKit/components/CopyButton.vue'
import DataTableV2, { type TableColumn } from '@uiKit/components/DataTableV2.vue'

defineProps<{
  rows: CryptoWallet[]
  loading: boolean
  total: number
  page: number
  pageSize: number
  search: string
}>()

const emit = defineEmits<{
  (e: 'update:search', value: string): void
  (e: 'update:page', value: number): void
}>()

const columns: TableColumn[] = [
  { key: 'investor', label: 'INVESTOR', width: '220px' },
  { key: 'asset', label: 'ASSET', width: '100px' },
  { key: 'network', label: 'NETWORK', width: '120px' },
  { key: 'walletAddress', label: 'ADDRESS', width: '200px' },
  { key: 'trustlineStatus', label: 'TRUSTLINE', width: '140px' },
  // Independent of TRUSTLINE: a line can be `active` and still unauthorised by
  // the issuer, which is what parks an order at `processing_opt_in` (WAL-06).
  { key: 'issuerAuth', label: 'ISSUER AUTH', width: '150px' },
  { key: 'balance', label: 'BALANCE', width: '140px', align: 'right' },
  { key: 'txHash', label: 'TRUSTSET TX', width: '180px' },
  { key: 'status', label: 'STATUS', width: '120px' },
  { key: 'createdAt', label: 'CREATED', width: '140px' },
]

// 64 hex characters do not fit a cell, and head-only truncation drops the tail
// that distinguishes two hashes. Full value stays on the copy button.
const TX_HASH_HEAD = 8
const TX_HASH_TAIL = 6

function investorFullName(row: CryptoWallet): string {
  if (!row.investor) return '—'
  const name = `${row.investor.firstName ?? ''} ${row.investor.lastName ?? ''}`.trim()
  return name || '—'
}

function getTrustlineColor(status: string): 'success' | 'warning' | 'danger' | 'neutral' | 'info' {
  const s = status?.toLowerCase()
  if (s === 'active' || s === 'verified') return 'success'
  if (s === 'pending' || s === 'in_progress') return 'warning'
  if (s === 'rejected' || s === 'failed') return 'danger'
  return 'neutral'
}

function getStatusColor(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  const s = status?.toLowerCase()
  if (s === 'active') return 'success'
  if (s === 'frozen' || s === 'pending') return 'warning'
  if (s === 'closed' || s === 'blocked') return 'danger'
  return 'neutral'
}
</script>

<template>
  <DataTableV2
    :columns="columns"
    :rows="rows"
    :loading="loading"
    title="Crypto wallets"
    :subtitle="`${total} investor crypto wallets`"
    row-key="id"
    search-placeholder="Search investor, asset, address..."
    :local-search="false"
    :local-pagination="false"
    :page="page"
    :page-size="pageSize"
    :total="total"
    :search="search"
    empty-title="No crypto wallets"
    empty-subtitle="Investor crypto wallets will appear here."
    @update:search="emit('update:search', $event)"
    @update:page="emit('update:page', $event)"
  >
    <template #cell-investor="{ row }">
      <Box flex direction="col">
        <Text type="body" weight="semibold">{{ investorFullName(row as CryptoWallet) }}</Text>
        <Text type="caption" color="muted">
          {{ (row as CryptoWallet).investor?.email ?? '—' }}
        </Text>
      </Box>
    </template>

    <template #cell-asset="{ row }">
      <Text type="body-sm" weight="medium">{{ (row as CryptoWallet).asset || '—' }}</Text>
    </template>

    <template #cell-network="{ row }">
      <Text type="body-sm">{{ (row as CryptoWallet).network || '—' }}</Text>
    </template>

    <template #cell-walletAddress="{ row }">
      <Text type="body-sm" color="muted">
        {{ truncate((row as CryptoWallet).walletAddress ?? '', 18) }}
      </Text>
    </template>

    <template #cell-trustlineStatus="{ row }">
      <StatusBadge
        :color="getTrustlineColor((row as CryptoWallet).trustlineStatus)"
        :label="formatKey((row as CryptoWallet).trustlineStatus ?? '')"
      />
    </template>

    <!-- Three-state. A null renders `neutral` "Not checked" — never a failure
         tint, which would report a refusal nobody has looked for. -->
    <template #cell-issuerAuth="{ row }">
      <StatusBadge
        :color="peerAuthorizationColor((row as CryptoWallet).peerAuthorized)"
        :label="peerAuthorizationLabel((row as CryptoWallet).peerAuthorized)"
      />
    </template>

    <template #cell-balance="{ row }">
      <Text type="body-sm">{{ (row as CryptoWallet).balance }}</Text>
    </template>

    <template #cell-txHash="{ row }">
      <Box v-if="(row as CryptoWallet).txHash" flex align="center" gap="1">
        <Text type="caption" family="mono" class="tx-hash">
          {{ truncateMiddle((row as CryptoWallet).txHash, TX_HASH_HEAD, TX_HASH_TAIL) }}
        </Text>
        <CopyButton :value="(row as CryptoWallet).txHash" label="Copy" />
      </Box>
      <Text v-else type="caption" color="muted">Not submitted</Text>
    </template>

    <template #cell-status="{ row }">
      <StatusBadge
        :color="getStatusColor((row as CryptoWallet).status)"
        :label="formatKey((row as CryptoWallet).status ?? '')"
      />
    </template>

    <template #cell-createdAt="{ row }">
      <Text type="body-sm" color="muted">{{ formatDate((row as CryptoWallet).createdAt) }}</Text>
    </template>
  </DataTableV2>
</template>

<style scoped>
.tx-hash {
  white-space: nowrap;
}
</style>
